import { Component, signal, OnInit, input, model } from '@angular/core';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ModalsService } from '@core/services/modals/modals.service';
import { RegistrationStep } from '@shared/modals/como/como-registration-modal/registration-step.enum';
import { Router } from '@angular/router';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { SettingsParametersState } from '@store/settings';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { map, Observable } from 'rxjs';
import { ComoPluginType } from '@core/models/plugin.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { FidelityType } from '@shared/modals/kiosk-fidelity-registration/fidelity-type.enum';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';

export type ComoRegistrationModalConfig = ModalConfig<{
  handle: typeof ComoRegistrationModalComponent.handle;
  inputs: {
    goToURLOnClose: string;
    goToKioskRegister?: boolean;
    activeStep?: RegistrationStep;
    accentColor?: ContentBuilderFieldColors;
  };
}>;

@Component({
  selector: 'app-como-registration-modal',
  templateUrl: './como-registration-modal.component.html',
  styleUrls: ['./como-registration-modal.component.scss'],
})
export class ComoRegistrationModalComponent
  extends DestroyerBase
  implements OnInit, GetModalInterface<ComoRegistrationModalConfig>
{
  static handle = 'como-registration-modal' as const;

  phone = signal<string>('');

  registrationStep = RegistrationStep;

  protected readonly FidelityType = FidelityType;

  constructor(
    private readonly modalsService: ModalsService,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }
  public readonly goToURLOnClose = input.required<string>();
  public readonly goToKioskRegister = input<boolean>();
  public readonly activeStep = model<RegistrationStep>(this.registrationStep.STEP_PHONE);
  public readonly accentColor = input<ContentBuilderFieldColors>();

  private readonly comoPlugin$: Observable<ComoPluginType | undefined> =
    this.marketplaceSelector.selectMarketplacePlugins.pipe(
      map(plugins => plugins?.find(plugin => plugin.type === 'como') as ComoPluginType | undefined),
    );

  protected readonly useLocalFormat = toSignal(
    this.comoPlugin$.pipe(map(plugin => !!(plugin?.phone_number_format === 'LOCAL'))),
    { initialValue: true },
  );
  protected readonly defaultRegionCode = toSignal(
    this.comoPlugin$.pipe(map(plugin => plugin?.phone_number_default_region_code ?? 'fr')),
    { initialValue: 'fr' },
  );

  ngOnInit() {
    this.getParameters();
    if (this.goToKioskRegister()) {
      this.onGoToRegisterStep();
    }
  }

  onClose(): void {
    this.modalsService.close(ComoRegistrationModalComponent.handle);
  }

  onComplete(): void {
    this.onClose();
    if (this.goToURLOnClose()) {
      this.analyticsService.log({
        level: 'debug',
        label: '#DOOD-1250 Navigate',
        message: {
          from: 'ComoRegistrationModalComponent Ligne°73',
          target: [
            this.goToURLOnClose() ? this.routerHelper.translateRoute(this.goToURLOnClose()) : '',
          ],
          currentLocation: window.location.pathname,
        },
      });
      this.router.navigate([
        this.goToURLOnClose() ? this.routerHelper.translateRoute(this.goToURLOnClose()) : '',
      ]);
    }
  }

  onGoToOtpStep(): void {
    this.activeStep.set(this.registrationStep.STEP_OTP);
  }

  onGoToAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.STEP_ACCOUNT_NOT_FOUND);
  }

  onGoToPhoneStep() {
    this.activeStep.set(this.registrationStep.STEP_PHONE);
  }

  onGoToAuthenticationStep() {
    this.activeStep.set(this.registrationStep.STEP_AUTHENTICATION);
  }

  onGoToConfirmationStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_CONFIRMATION);
  }

  onGoToRegisterStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_REGISTER);
  }

  onGoToKioskRegisterConfirmationStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_REGISTER_CONFIRMATION);
  }

  onGoToKioskAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_ACCOUNT_NOT_FOUND);
  }

  getParameters(): SettingsParametersState {
    return this.settingsSelector.parameters;
  }
}
